import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sortOrganisations } from "@/utils/organisationSort";
import { supabase } from "@/integrations/supabase/client";
import { useRoleBasedAccess } from "./use-role-based-access";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { generateSlug } from "@/utils/slugify";
import { resolveUniqueSlug, isUniqueViolation } from "@/utils/uniqueSlug";
import { updateDeviceField } from "@/utils/updateDeviceField";
import { useDeviceChangeLog } from "./use-device-change-log";

export type MainOrgCamera = {
  device_id: string;
  device_name: string | null;
  device_serial: string | null;
  device_status: string | null;
  enable_public_view: boolean | null;
};

/** Toggle a camera's public view (same as Device Config "Enable Public View"). */
export const useSetDevicePublic = (parentId?: string | null) => {
  const qc = useQueryClient();
  const { logChange } = useDeviceChangeLog();
  const key = ["sub-org-main-cameras", parentId];
  return useMutation({
    mutationFn: async ({ deviceId, isPublic }: { deviceId: string; isPublic: boolean }) => {
      const v = await updateDeviceField(deviceId, "enablePublic", isPublic ? 1 : 0);
      logChange(deviceId, "enablePublic", v);
    },
    onMutate: async ({ deviceId, isPublic }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<MainOrgCamera[]>(key);
      qc.setQueryData<MainOrgCamera[]>(key, (old) =>
        (old || []).map((c) => (c.device_id === deviceId ? { ...c, enable_public_view: isPublic } : c)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error("Couldn't change public view");
    },
    onSuccess: () => toast.success("Device updated successfully"),
    // Camera confirms via webhook shortly after; refresh then
    onSettled: () => setTimeout(() => qc.invalidateQueries({ queryKey: key }), 4000),
  });
};

// New tables/columns are not in the generated types yet.
const db = supabase as any;

export type SubOrgRole = "standard" | "editor";

export interface SubOrganization {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  address: string | null;
  country: string | null;
  website: string | null;
  industry: string | null;
  description: string | null;
  opening_times: string | null;
  marketing_banner_url: string | null;
  image_1_url: string | null;
  image_2_url: string | null;
  image_3_url: string | null;
  visibility: string;
  showcase_access_code: string | null;
  showcase_access_code_enabled: boolean;
  parent_organization_id: string;
  archived_at: string | null;
  created_at: string;
}

/** Who am I in the sub-organisation model? */
export const useSubOrgContext = () => {
  const { userOrganizationId, isSystemAdmin, isOrgAdmin, isLoading } = useRoleBasedAccess();
  const orgQuery = useQuery({
    queryKey: ["sub-org-context", userOrganizationId],
    queryFn: async () => {
      if (!userOrganizationId) return null;
      const { data, error } = await db
        .from("organizations")
        .select("id, name, parent_organization_id")
        .eq("id", userOrganizationId)
        .maybeSingle();
      if (error) {
        console.error("Sub-org context error:", error);
        return null;
      }
      return data as { id: string; name: string; parent_organization_id: string | null } | null;
    },
    enabled: !!userOrganizationId,
    staleTime: 60_000,
  });

  const isSubOrgUser = !!orgQuery.data?.parent_organization_id;
  const isMainOrgUser = !!orgQuery.data && !isSubOrgUser;
  return {
    isLoading: isLoading || orgQuery.isLoading,
    ownOrganization: orgQuery.data,
    isSubOrgUser,
    isMainOrgUser,
    canView: isSystemAdmin || isMainOrgUser,
    canManage: isSystemAdmin || (isMainOrgUser && isOrgAdmin),
    isSystemAdmin,
  };
};

export const useMainOrganizationsForPicker = (enabled: boolean) =>
  useQuery({
    queryKey: ["sub-org-main-orgs"],
    queryFn: async () => {
      const { data, error } = await db
        .from("organizations")
        .select("id, name")
        .is("parent_organization_id", null)
        .neq("id", "00000000-0000-0000-0000-000000000000")
        .order("name");
      if (error) throw error;
      return sortOrganisations((data || []) as { id: string; name: string }[], org => org.name);
    },
    enabled,
  });

export const useSubOrganizations = (parentId?: string | null, includeArchived = false) =>
  useQuery({
    queryKey: ["sub-organizations", parentId, includeArchived],
    queryFn: async () => {
      let q = db.from("organizations").select("*").eq("parent_organization_id", parentId).order("name");
      if (!includeArchived) q = q.is("archived_at", null);
      const { data: orgs, error } = await q;
      if (error) throw error;
      const ids = (orgs || []).map((o: any) => o.id);
      const counts: Record<string, { users: number; cameras: number }> = {};
      ids.forEach((id: string) => (counts[id] = { users: 0, cameras: 0 }));
      if (ids.length) {
        const [{ data: roles }, { data: devs }] = await Promise.all([
          db.from("user_roles").select("organization_id").in("organization_id", ids),
          db.from("sub_organization_devices").select("sub_organization_id").in("sub_organization_id", ids),
        ]);
        (roles || []).forEach((r: any) => counts[r.organization_id] && counts[r.organization_id].users++);
        (devs || []).forEach((d: any) => counts[d.sub_organization_id] && counts[d.sub_organization_id].cameras++);
      }
      return sortOrganisations((orgs || []).map((o: any) => ({ ...o, ...counts[o.id] })) as (SubOrganization & {
        users: number;
        cameras: number;
      })[], org => org.name);
    },
    enabled: !!parentId,
  });

export const useSubOrganization = (id?: string) =>
  useQuery({
    queryKey: ["sub-organization", id],
    queryFn: async () => {
      const { data, error } = await db.from("organizations").select("*").eq("id", id).maybeSingle();
      if (error) throw error;
      return data as SubOrganization | null;
    },
    enabled: !!id,
  });

const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["sub-organizations"] });
  qc.invalidateQueries({ queryKey: ["sub-organization"] });
  qc.invalidateQueries({ queryKey: ["sub-org-devices"] });
  qc.invalidateQueries({ queryKey: ["sub-org-users"] });
};

export const useCreateSubOrganization = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parentId, name, ...rest }: { parentId: string; name: string } & Partial<SubOrganization>) => {
      const insert = (slug: string) =>
        db
          .from("organizations")
          .insert({ name, slug, parent_organization_id: parentId, visibility: "private", ...rest })
          .select()
          .single();
      let { data, error } = await insert(await resolveUniqueSlug(name));
      if (error && isUniqueViolation(error)) {
        // Address taken by an organisation this user can't see — add a short unique suffix
        const fallback = `${generateSlug(name) || "organisation"}-${crypto.randomUUID().slice(0, 6)}`;
        ({ data, error } = await insert(fallback));
      }
      if (error) throw error;
      return data as SubOrganization;
    },
    onSuccess: () => {
      invalidateAll(qc);
      toast.success("Sub-customer created");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create sub-customer"),
  });
};

export const useUpdateSubOrganization = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SubOrganization> }) => {
      const { parent_organization_id, id: _i, created_at, ...safe } = data as any;
      const { error } = await db.from("organizations").update(safe).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll(qc);
      toast.success("Sub-customer saved");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to save"),
  });
};

export const useArchiveSubOrganization = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archive }: { id: string; archive: boolean }) => {
      const { error } = await db
        .from("organizations")
        .update({ archived_at: archive ? new Date().toISOString() : null, ...(archive ? { visibility: "private" } : {}) })
        .eq("id", id);
      if (error) throw error;
      // Archiving hides cameras from sub-customer users; restoring gives them back.
      const userIds = await getSubOrgUserIds(id);
      const { data: devs } = await db.from("sub_organization_devices").select("device_id").eq("sub_organization_id", id);
      const deviceIds = (devs || []).map((d: any) => d.device_id);
      if (archive) await removeAssignments(userIds, deviceIds);
      else await addAssignments(userIds, deviceIds);
    },
    onSuccess: (_d, v) => {
      invalidateAll(qc);
      toast.success(v.archive ? "Sub-customer archived" : "Sub-customer restored");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update"),
  });
};

// ---------- Devices ----------

const getSubOrgUserIds = async (subOrgId: string): Promise<string[]> => {
  const { data } = await db.from("user_roles").select("user_id").eq("organization_id", subOrgId);
  return (data || []).map((r: any) => r.user_id);
};

const addAssignments = async (userIds: string[], deviceIds: string[]) => {
  if (!userIds.length || !deviceIds.length) return;
  const { data: existing } = await db
    .from("device_assignments")
    .select("user_id, device_id")
    .in("user_id", userIds)
    .in("device_id", deviceIds);
  const have = new Set((existing || []).map((e: any) => `${e.user_id}|${e.device_id}`));
  const rows = userIds.flatMap((u) =>
    deviceIds.filter((d) => !have.has(`${u}|${d}`)).map((d) => ({ user_id: u, device_id: d })),
  );
  if (rows.length) {
    const { error } = await db.from("device_assignments").insert(rows);
    if (error) throw error;
  }
};

const removeAssignments = async (userIds: string[], deviceIds: string[]) => {
  if (!userIds.length || !deviceIds.length) return;
  const { error } = await db.from("device_assignments").delete().in("user_id", userIds).in("device_id", deviceIds);
  if (error) throw error;
};

/** Cameras owned by the main organisation (candidates for allocation). */
export const useMainOrgCameras = (parentId?: string | null) =>
  useQuery({
    queryKey: ["sub-org-main-cameras", parentId],
    queryFn: async () => {
      const { data, error } = await db
        .from("device_details")
        .select("device_id, device_name, device_serial, device_status, enable_public_view")
        .eq("organization_id", parentId)
        .order("device_name");
      if (error) throw error;
      return (data || []) as MainOrgCamera[];
    },
    enabled: !!parentId,
  });

export const useSubOrgDevices = (subOrgId?: string) =>
  useQuery({
    queryKey: ["sub-org-devices", subOrgId],
    queryFn: async () => {
      const { data, error } = await db
        .from("sub_organization_devices")
        .select("device_id, allocated_at")
        .eq("sub_organization_id", subOrgId);
      if (error) throw error;
      return (data || []) as { device_id: string; allocated_at: string }[];
    },
    enabled: !!subOrgId,
  });

/** All allocations under a parent, to show which sub-customers share each camera. */
export const useParentAllocations = (subOrgIds: string[]) =>
  useQuery({
    queryKey: ["sub-org-devices", "parent", subOrgIds.join(",")],
    queryFn: async () => {
      if (!subOrgIds.length) return [];
      const { data, error } = await db
        .from("sub_organization_devices")
        .select("device_id, sub_organization_id")
        .in("sub_organization_id", subOrgIds);
      if (error) throw error;
      return (data || []) as { device_id: string; sub_organization_id: string }[];
    },
  });

export const useSetSubOrgDevice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ subOrgId, deviceId, allocate }: { subOrgId: string; deviceId: string; allocate: boolean }) => {
      const userIds = await getSubOrgUserIds(subOrgId);
      if (allocate) {
        const { error } = await db.from("sub_organization_devices").insert({ sub_organization_id: subOrgId, device_id: deviceId });
        if (error && error.code !== "23505") throw error;
        await addAssignments(userIds, [deviceId]);
      } else {
        const { error } = await db
          .from("sub_organization_devices")
          .delete()
          .eq("sub_organization_id", subOrgId)
          .eq("device_id", deviceId);
        if (error) throw error;
        await removeAssignments(userIds, [deviceId]);
      }
    },
    onSuccess: (_d, v) => {
      invalidateAll(qc);
      qc.invalidateQueries({ queryKey: ["user-devices"] });
      toast.success(v.allocate ? "Camera shared" : "Camera removed");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update camera"),
  });
};

// ---------- Users ----------

export interface SubOrgUser {
  user_id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export const useSubOrgUsers = (subOrgId?: string) =>
  useQuery({
    queryKey: ["sub-org-users", subOrgId],
    queryFn: async () => {
      const { data: roles, error } = await db.from("user_roles").select("user_id, role").eq("organization_id", subOrgId);
      if (error) throw error;
      const ids = (roles || []).map((r: any) => r.user_id);
      let profiles: any[] = [];
      if (ids.length) {
        const { data } = await db.from("profiles").select("id, first_name, last_name, email").in("id", ids);
        profiles = data || [];
      }
      const users: SubOrgUser[] = (roles || []).map((r: any) => {
        const p = profiles.find((x) => x.id === r.user_id) || {};
        return { user_id: r.user_id, role: r.role, first_name: p.first_name ?? null, last_name: p.last_name ?? null, email: p.email ?? null };
      });
      const { data: invites } = await db
        .from("user_invitations")
        .select("id, email, invited_role, status, created_at, expires_at")
        .eq("organization_id", subOrgId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      return { users, invites: (invites || []) as any[] };
    },
    enabled: !!subOrgId,
  });

export const useUpdateSubOrgUserRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ subOrgId, userId, role }: { subOrgId: string; userId: string; role: SubOrgRole }) => {
      const { error } = await db.from("user_roles").update({ role }).eq("user_id", userId).eq("organization_id", subOrgId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll(qc);
      toast.success("Access level updated");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update access level"),
  });
};

export const useRemoveSubOrgUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ subOrgId, userId }: { subOrgId: string; userId: string }) => {
      const { data: devs } = await db.from("sub_organization_devices").select("device_id").eq("sub_organization_id", subOrgId);
      await removeAssignments([userId], (devs || []).map((d: any) => d.device_id));
      const { error } = await db.from("user_roles").delete().eq("user_id", userId).eq("organization_id", subOrgId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll(qc);
      toast.success("User removed from sub-customer");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to remove user"),
  });
};

export const buildSubOrgInviteLink = (invitationId: string, email: string) =>
  `${window.location.origin}/onboarding?token=${invitationId}&email=${encodeURIComponent(email)}`;

export const useInviteSubOrgUser = () => {
  const qc = useQueryClient();
  const { session } = useAuth();
  return useMutation({
    mutationFn: async ({ subOrg, email, role }: { subOrg: SubOrganization; email: string; role: SubOrgRole }) => {
      const cleanEmail = email.trim().toLowerCase();
      const { data: devs } = await db.from("sub_organization_devices").select("device_id").eq("sub_organization_id", subOrg.id);
      const { data: invitation, error } = await db
        .from("user_invitations")
        .upsert(
          [{
            organization_id: subOrg.id,
            email: cleanEmail,
            invited_role: role,
            device_ids: (devs || []).map((d: any) => d.device_id),
            invited_by: session?.user.id,
            status: "pending",
          }],
          { onConflict: "email,organization_id" },
        )
        .select()
        .single();
      if (error || !invitation) throw error || new Error("Invitation failed");
      const inviteLink = buildSubOrgInviteLink(invitation.id, cleanEmail);
      const { error: emailError } = await supabase.functions.invoke("send-invitation", {
        body: {
          email: cleanEmail,
          organizationName: subOrg.name,
          inviteLink,
          contactId: "",
          invitationToken: invitation.id,
          organizationId: subOrg.id,
          invitedRole: role,
          baseUrl: window.location.origin,
        },
      });
      return { inviteLink, emailFailed: !!emailError };
    },
    onSuccess: (r) => {
      invalidateAll(qc);
      if (r.emailFailed) toast.warning("Invitation created, but the email failed. Use Copy link instead.");
      else toast.success("Invitation sent");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to invite user"),
  });
};

export const useCancelSubOrgInvite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from("user_invitations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAll(qc);
      toast.success("Invitation cancelled");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to cancel invitation"),
  });
};
